import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Menu as MenuIcon, Search, Bell, Download, LogOut, User } from 'lucide-react'
import { useInstallPrompt } from '@/hooks/useInstallPrompt'
import toast from 'react-hot-toast'
import { useAuth, getDashboardBusiness } from '@cravo/auth'
import { notificationsApi, vendorDashboardApi } from '@cravo/api-service'
import { useTranslation } from '@cravo/i18n'
import { NOTIFICATION_UNREAD_EVENT } from '@/lib/dashboard-events'
import { partnerAppName } from '@/lib/tenant'

export function Header({ title, onMenuClick }: { title: string; onMenuClick?: () => void }) {
  const { t } = useTranslation()
  const { vendor, logout } = useAuth()
  const navigate = useNavigate()
  const [status, setStatus] = useState<'online' | 'offline'>('offline')
  const [busy, setBusy] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [notificationsBusy, setNotificationsBusy] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    let cancelled = false

    async function hydrateHeaderState() {
      try {
        const [dashboard, unread] = await Promise.all([
          vendorDashboardApi.get(),
          notificationsApi.getUnreadCount().catch(() => ({ count: 0 })),
        ])
        if (!cancelled) {
          setStatus(dashboard.business?.isOnline ? 'online' : 'offline')
          setUnreadCount(unread.count ?? 0)
        }
      } catch {
        // Keep the existing fallback state if dashboard bootstrap fails.
      }
    }

    void hydrateHeaderState()

    const pollNotifications = () => {
      if (document.visibilityState === 'hidden') return
      void notificationsApi.getUnreadCount()
        .then((response) => {
          if (!cancelled) setUnreadCount(response.count ?? 0)
        })
        .catch(() => {})
    }
    const pollId = window.setInterval(pollNotifications, 30_000)
    document.addEventListener('visibilitychange', pollNotifications)

    function handleStatusSync(event: Event) {
      const detail = (event as CustomEvent<{ isOnline?: boolean }>).detail
      if (typeof detail?.isOnline === 'boolean') {
        setStatus(detail.isOnline ? 'online' : 'offline')
      }
    }

    function handleUnreadSync(event: Event) {
      const detail = (event as CustomEvent<{ count?: number }>).detail
      if (typeof detail?.count === 'number') {
        setUnreadCount(detail.count)
      }
    }

    window.addEventListener('vendor-dashboard-status', handleStatusSync as EventListener)
    window.addEventListener(NOTIFICATION_UNREAD_EVENT, handleUnreadSync as EventListener)

    return () => {
      cancelled = true
      window.clearInterval(pollId)
      document.removeEventListener('visibilitychange', pollNotifications)
      window.removeEventListener('vendor-dashboard-status', handleStatusSync as EventListener)
      window.removeEventListener(NOTIFICATION_UNREAD_EVENT, handleUnreadSync as EventListener)
    }
  }, [])

  const handleLogout = async () => {
    await logout()
    navigate('/welcome')
  }

  const handleToggleStatus = async () => {
    const businessId = getDashboardBusiness(vendor)?.id
    if (!businessId || busy) return

    const nextStatus = status === 'online' ? 'offline' : 'online'
    setStatus(nextStatus)
    setBusy(true)
    window.dispatchEvent(
      new CustomEvent('vendor-dashboard-status', {
        detail: { isOnline: nextStatus === 'online', businessId },
      }),
    )

    try {
      await vendorDashboardApi.updateBusinessStatus(businessId, nextStatus === 'online')
    } catch {
      const rollback = nextStatus === 'online' ? 'offline' : 'online'
      setStatus(rollback)
      window.dispatchEvent(
        new CustomEvent('vendor-dashboard-status', {
          detail: { isOnline: rollback === 'online', businessId },
        }),
      )
    } finally {
      setBusy(false)
    }
  }

  const initial = vendor?.fullName?.charAt(0)?.toUpperCase() || 'V'

  const handleNotificationsClick = async () => {
    if (notificationsBusy) return
    navigate('/notifications')
  }

  return (
    <div className="topbar">
      <button
        className="hamburger"
        onClick={() => onMenuClick?.()}
        aria-label={t('common.openMenu')}
        type="button"
      >
        <MenuIcon size={20} />
      </button>
      <h1>{title}</h1>
      <div className="topbar-spacer" style={{ flex: 1 }} />

      <button
        className={`status-toggle ${status === 'offline' ? 'off' : ''}`}
        onClick={() => void handleToggleStatus()}
        disabled={busy}
      >
        <span className="status-dot" />
        <span className="status-label">
          {busy
            ? t('header.status.updating')
            : status === 'online'
              ? t('header.status.online')
              : t('header.status.offline')}
        </span>
      </button>

      <button className="btn hide-on-mobile" style={{ padding: '8px 10px' }}>
        <Search size={18} />
      </button>

      <button className="btn" style={{ padding: '8px 10px', position: 'relative' }} onClick={() => void handleNotificationsClick()} disabled={notificationsBusy}>
        <Bell size={18} />
        {unreadCount > 0 && (
          <>
            <span
              style={{
                position: 'absolute',
                top: 4,
                right: 6,
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: 'var(--brand-pink)',
              }}
            />
            <span
              style={{
                position: 'absolute',
                top: -6,
                right: -4,
                minWidth: 18,
                height: 18,
                borderRadius: 999,
                background: 'var(--brand-pink)',
                color: 'white',
                fontSize: 10,
                fontWeight: 700,
                display: 'grid',
                placeItems: 'center',
                padding: '0 4px',
              }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          </>
        )}
      </button>

      <div ref={menuRef} style={{ position: 'relative' }}>
        <div
          className="avatar"
          style={{ cursor: 'pointer' }}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {initial}
        </div>

        {menuOpen && (
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: 44,
              width: 200,
              background: 'white',
              borderRadius: 12,
              boxShadow: 'var(--shadow-lg)',
              border: '1px solid var(--ink-150)',
              padding: 8,
              zIndex: 100,
            }}
          >
            <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--ink-150)', marginBottom: 4 }}>
              <div style={{ fontSize: 13, fontWeight: 600, overflowWrap: 'anywhere' }}>{vendor?.fullName}</div>
              {/* Email is an unbroken token (no spaces); without overflow-wrap it
                  ran past the card's padding to the edge. Wrap it so the full
                  address stays inside the card with the normal 12px gutter. */}
              <div style={{ fontSize: 11, color: 'var(--ink-500)', overflowWrap: 'anywhere', lineHeight: 1.4 }} title={vendor?.email ?? undefined}>
                {vendor?.email}
              </div>
            </div>
            <InstallMenuItem onActivate={() => setMenuOpen(false)} />
            <button
              onClick={() => { setMenuOpen(false); navigate('/settings') }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', border: 'none', background: 'none',
                cursor: 'pointer', borderRadius: 8, fontSize: 13, color: 'var(--ink-700)',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--ink-50)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
            >
              <User size={16} /> {t('header.menu.profileAndSettings')}
            </button>
            <button
              onClick={handleLogout}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', border: 'none', background: 'none',
                cursor: 'pointer', borderRadius: 8, fontSize: 13, color: 'var(--danger)',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--danger-bg)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
            >
              <LogOut size={16} /> {t('header.menu.signOut')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function InstallMenuItem({ onActivate }: { onActivate: () => void }) {
  const { t } = useTranslation()
  const { installed, canInstallNatively, supportLevel, install } = useInstallPrompt()
  if (installed) return null
  if (supportLevel === 'manual-only' && !canInstallNatively) return null

  const handleClick = async () => {
    onActivate()
    if (canInstallNatively) {
      const outcome = await install()
      if (outcome === 'unavailable') {
        toast(t('header.menu.installUnavailable'), { icon: 'ℹ️', duration: 5000 })
      }
    } else if (supportLevel === 'ios-safari') {
      toast(t('header.menu.iosSafariHint'), { icon: '📱', duration: 6000 })
    }
  }

  const label = canInstallNatively
    ? t('header.menu.installApp', { appName: partnerAppName() })
    : t('header.menu.installOnDevice')

  return (
    <button
      onClick={() => void handleClick()}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 12px', border: 'none', background: 'none',
        cursor: 'pointer', borderRadius: 8, fontSize: 13, color: 'var(--ink-700)',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--ink-50)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
    >
      <Download size={16} /> {label}
    </button>
  )
}
